/**
 * DS-Survey API Service
 * API client for Survey module
 */

import { TRMApiClient } from '@/modules/utility/shared/utils/api-client';
import type {
  FieldType,
  TargetType,
  Survey,
  SurveyWithDetails,
  Section,
  Question,
  Response,
  ResponseWithAnswers,
  Answer,
  SurveyDashboardStats,
  Role,
  BoundaryType,
  RoleAssignment,
  GeneratedTable,
  GeneratedColumn,
  TableGenerationResult,
  ColumnMapping,
  DynamicResponse,
  DynamicResponseFilters,
  ValidatorHierarchy,
  ValidatorHierarchyConfig,
  CanValidateResult,
  ValidationActionPayload,
  ValidatorSurvey,
  BulkValidationPayload,
  ValidationResult,
  BulkValidationResult,
  PendingValidation,
  ValidationStats,
  SurveyStatsResponse,
  EnrichedResponseData,
  SurveyorDashboardData,
  SurveyorObligation,
} from '../types/survey.types';

// Create API client instance for ds-survey module
const dsSurveyApiClient = new TRMApiClient('ds-survey');

/**
 * Current portal site id for the logged-in user, used to scope survey lists.
 * Survey rows store the portal site (prvdr_ste_id) in their site_id column, so
 * that is what we resolve here, in order of reliability:
 *   1. user_data.siteId — the canonical siteId the backend stamps at login
 *      (ds_prjct_lst_t.ste_id). Null on deployments where the project lookup
 *      isn't populated.
 *   2. site_details.prvdr_ste_id — the portal site captured client-side
 *      at login. This IS the survey site_id, so it's a reliable fallback when
 *      user_data.siteId is null.
 * Returns undefined when unknown (→ no site filter, preserving prior behaviour).
 */
export const getCurrentSiteId = (): number | undefined => {
  const readNum = (v: any): number | undefined =>
    v != null && v !== '' && !isNaN(Number(v)) ? Number(v) : undefined;
  try {
    const rawUser = localStorage.getItem('user_data');
    if (rawUser) {
      const fromUser = readNum(JSON.parse(rawUser)?.siteId);
      if (fromUser !== undefined) return fromUser;
    }
    const rawSite = localStorage.getItem('site_details') || localStorage.getItem('apufidc_site_details');
    if (rawSite) {
      const fromSite = readNum(JSON.parse(rawSite)?.prvdr_ste_id);
      if (fromSite !== undefined) return fromSite;
    }
    return undefined;
  } catch {
    return undefined;
  }
};

// ========================================
// FIELD TYPE MASTER APIs
// ========================================
export const fieldTypeApi = {
  getAll: async (): Promise<FieldType[]> => {
    const response = await dsSurveyApiClient.getFromModule('/field-type');
    return response.data || [];
  },

  getActive: async (): Promise<FieldType[]> => {
    const response = await dsSurveyApiClient.getFromModule('/field-type/active');
    return response.data || [];
  },

  getById: async (id: number): Promise<FieldType | null> => {
    const response = await dsSurveyApiClient.getFromModule(`/field-type/${id}`);
    return response.data;
  },

  getByCode: async (code: string): Promise<FieldType | null> => {
    const response = await dsSurveyApiClient.getFromModule(`/field-type/code/${code}`);
    return response.data;
  },

  save: async (fieldType: Partial<FieldType>): Promise<FieldType> => {
    const response = await dsSurveyApiClient.postToModule('/field-type', fieldType);
    return response.data;
  },

  delete: async (id: number): Promise<void> => {
    await dsSurveyApiClient.deleteFromModule(`/field-type/${id}`);
  },
};

// ========================================
// TARGET TYPE MASTER APIs
// ========================================
export const targetTypeApi = {
  getAll: async (): Promise<TargetType[]> => {
    const response = await dsSurveyApiClient.getFromModule('/target-type');
    return response.data || [];
  },

  getActive: async (): Promise<TargetType[]> => {
    const response = await dsSurveyApiClient.getFromModule('/target-type/active');
    return response.data || [];
  },

  getById: async (id: number): Promise<TargetType | null> => {
    const response = await dsSurveyApiClient.getFromModule(`/target-type/${id}`);
    return response.data;
  },

  getByCode: async (code: string): Promise<TargetType | null> => {
    const response = await dsSurveyApiClient.getFromModule(`/target-type/code/${code}`);
    return response.data;
  },

  save: async (targetType: Partial<TargetType>): Promise<TargetType> => {
    const response = await dsSurveyApiClient.postToModule('/target-type', targetType);
    return response.data;
  },

  delete: async (id: number): Promise<void> => {
    await dsSurveyApiClient.deleteFromModule(`/target-type/${id}`);
  },
};

// ========================================
// API ROUTE LOOKUP APIs
// ========================================
export interface ApiRoute {
  srvy_api_rte_id: number;
  rte_nm: string;
  rte_cd: string;
  rte_url: string;
  dscn_tx?: string;
  vlu_clmn_nm?: string;
  lbl_clmn_nm?: string;
  rspns_path_tx?: string;
}

export const apiRouteApi = {
  getActive: async (): Promise<ApiRoute[]> => {
    const response = await dsSurveyApiClient.getFromModule('/api-route');
    return response.data || [];
  },

  getById: async (id: number): Promise<ApiRoute | null> => {
    const response = await dsSurveyApiClient.getFromModule(`/api-route/${id}`);
    return response.data;
  },

  save: async (route: Partial<ApiRoute>): Promise<ApiRoute> => {
    const response = await dsSurveyApiClient.postToModule('/api-route', route);
    return response.data;
  },

  delete: async (id: number): Promise<void> => {
    await dsSurveyApiClient.deleteFromModule(`/api-route/${id}`);
  },

  /**
   * Fetch options from any API endpoint (for dynamic dropdowns)
   * @param apiUrl The API endpoint URL (e.g., '/api/admin/districts/list')
   * @param valueColumn The column name to use as the option value
   * @param labelColumn The column name to use as the option label
   * @param responsePath The path to the data array in the response (default: 'data')
   * @returns Array of {value, label} options
   */
  fetchOptionsFromEndpoint: async (
    apiUrl: string,
    valueColumn: string = 'id',
    labelColumn: string = 'name',
    responsePath: string = 'data'
  ): Promise<{ value: string; label: string }[]> => {
    try {
      // Use BaseAPIService directly for non-module URLs
      const { BaseAPIService } = await import('@/services/baseapiservice');
      const apiService = new BaseAPIService();
      const response = await apiService.get(apiUrl);

      // Extract data from response using responsePath
      let data = response?.data;

      // Navigate to the nested path if specified (e.g., 'data' or 'data.items')
      if (responsePath && data) {
        const pathParts = responsePath.split('.');
        for (const part of pathParts) {
          data = data?.[part];
        }
      }

      if (!Array.isArray(data)) {
        console.warn('API response is not an array:', apiUrl, data);
        return [];
      }

      // Map to options format
      return data.map((item: any) => ({
        value: String(item[valueColumn] ?? item.id ?? ''),
        label: String(item[labelColumn] ?? item.name ?? item[valueColumn] ?? ''),
      }));
    } catch (error) {
      console.error('Error fetching options from API:', apiUrl, error);
      return [];
    }
  },
};

// ========================================
// PORTAL SITES APIs
// ========================================
export const portalSitesApi = {
  getAll: async (): Promise<{ prvdr_ste_id: number; prvdr_ste_nm: string; ul_tx?: string; dscn_tx?: string; ste_nm?: string }[]> => {
    const response = await dsSurveyApiClient.getFromModule('/portal-sites');
    return response.data || [];
  },
};

// ========================================
// SURVEY APIs
// ========================================
export const surveyApi = {
  getAll: async (options?: { userCentric?: boolean; siteId?: number; skipSiteFilter?: boolean }): Promise<Survey[]> => {
    const params = new URLSearchParams();
    if (options?.userCentric) params.set('userCentric', 'true');
    // Site-scope by default: explicit siteId > current logged-in site. Callers that
    // genuinely need every site (e.g. copy-to-site source picker) pass skipSiteFilter.
    const effectiveSiteId = options?.skipSiteFilter ? undefined : (options?.siteId ?? getCurrentSiteId());
    if (effectiveSiteId) params.set('siteId', String(effectiveSiteId));
    if (options?.skipSiteFilter) params.set('skipSiteFilter', 'true');
    const qs = params.toString();
    const url = qs ? `/survey?${qs}` : '/survey';
    const response = await dsSurveyApiClient.getFromModule(url);
    return response.data || [];
  },

  getBySiteId: async (siteId: number): Promise<Survey[]> => {
    const response = await dsSurveyApiClient.getFromModule(`/survey/site/${siteId}`);
    return response.data || [];
  },

  getActive: async (options?: { siteId?: number; skipSiteFilter?: boolean }): Promise<Survey[]> => {
    const effectiveSiteId = options?.skipSiteFilter ? undefined : (options?.siteId ?? getCurrentSiteId());
    const url = effectiveSiteId ? `/survey/active?siteId=${effectiveSiteId}` : '/survey/active';
    const response = await dsSurveyApiClient.getFromModule(url);
    return response.data || [];
  },

  getById: async (id: number): Promise<Survey | null> => {
    const response = await dsSurveyApiClient.getFromModule(`/survey/${id}`);
    return response.data;
  },

  getWithDetails: async (id: number): Promise<SurveyWithDetails | null> => {
    const response = await dsSurveyApiClient.getFromModule(`/survey/${id}/full`);
    return response.data;
  },

  save: async (survey: Partial<Survey>): Promise<Survey> => {
    const response = await dsSurveyApiClient.postToModule('/survey', survey);
    return response.data;
  },

  updateStatus: async (id: number, status: string): Promise<Survey> => {
    const response = await dsSurveyApiClient.putToModule(`/survey/${id}/status`, { sts_cd: status });
    return response.data;
  },

  delete: async (id: number): Promise<void> => {
    await dsSurveyApiClient.deleteFromModule(`/survey/${id}`);
  },

  copyToSite: async (surveyId: number, payload: {
    target_site_id: number;
    new_survey_name?: string;
    new_survey_code?: string;
    copy_role_assignments?: boolean;
    generate_table?: boolean;
    copy_responses?: boolean;
  }): Promise<{
    new_survey_id: number;
    new_survey_code: string;
    sections_copied: number;
    questions_copied: number;
    role_assignments_copied: number;
    table_generated: boolean;
    table_error?: string;
    responses_copied: number;
    responses_error?: string;
  }> => {
    const response = await dsSurveyApiClient.postToModule(`/survey/${surveyId}/copy-to-site`, payload);
    return response.data;
  },
};

// ========================================
// SECTION APIs
// ========================================
export const sectionApi = {
  getBySurveyId: async (surveyId: number): Promise<Section[]> => {
    const response = await dsSurveyApiClient.getFromModule(`/section/survey/${surveyId}`);
    return response.data || [];
  },

  save: async (section: Partial<Section>): Promise<Section> => {
    const response = await dsSurveyApiClient.postToModule('/section', section);
    return response.data;
  },

  delete: async (id: number): Promise<void> => {
    await dsSurveyApiClient.deleteFromModule(`/section/${id}`);
  },
};

// ========================================
// QUESTION APIs
// ========================================
export const questionApi = {
  getById: async (id: number): Promise<Question | null> => {
    const response = await dsSurveyApiClient.getFromModule(`/question/${id}`);
    return response.data;
  },

  getBySurveyId: async (surveyId: number): Promise<Question[]> => {
    const response = await dsSurveyApiClient.getFromModule(`/question/survey/${surveyId}`);
    return response.data || [];
  },

  getBySectionId: async (sectionId: number): Promise<Question[]> => {
    const response = await dsSurveyApiClient.getFromModule(`/question/section/${sectionId}`);
    return response.data || [];
  },

  save: async (question: Partial<Question>): Promise<Question> => {
    const response = await dsSurveyApiClient.postToModule('/question', question);
    return response.data;
  },

  delete: async (id: number): Promise<void> => {
    await dsSurveyApiClient.deleteFromModule(`/question/${id}`);
  },
};

// ========================================
// RESPONSE APIs
// ========================================
export const responseApi = {
  getById: async (id: number): Promise<ResponseWithAnswers | null> => {
    const response = await dsSurveyApiClient.getFromModule(`/response/${id}`);
    return response.data;
  },

  getBySurveyId: async (surveyId: number): Promise<Response[]> => {
    const response = await dsSurveyApiClient.getFromModule(`/response/survey/${surveyId}`);
    return response.data || [];
  },

  getMyResponses: async (): Promise<Response[]> => {
    const response = await dsSurveyApiClient.getFromModule('/response/my');
    return response.data || [];
  },

  save: async (responseData: Partial<Response>): Promise<Response> => {
    const response = await dsSurveyApiClient.postToModule('/response', responseData);
    return response.data;
  },

  submitFull: async (responseData: { srvy_id: number; answers: Partial<Answer>[] }): Promise<Response> => {
    const response = await dsSurveyApiClient.postToModule('/response/submit-full', responseData);
    return response.data;
  },

  submit: async (id: number): Promise<Response> => {
    const response = await dsSurveyApiClient.putToModule(`/response/${id}/submit`, {});
    return response.data;
  },

  delete: async (id: number): Promise<void> => {
    await dsSurveyApiClient.deleteFromModule(`/response/${id}`);
  },
};

// ========================================
// ANSWER APIs
// ========================================
export const answerApi = {
  save: async (answer: Partial<Answer>): Promise<Answer> => {
    const response = await dsSurveyApiClient.postToModule('/answer', answer);
    return response.data;
  },
};

// ========================================
// ROLE ASSIGNMENT APIs
// ========================================

// Type for combined role IDs response
export interface CombinedRoleIds {
  surveyor_role_ids: number[];
  validator_role_ids: number[];
}

export const roleAssignmentApi = {
  /**
   * Get all active roles for dropdown selection
   */
  getRoles: async (): Promise<Role[]> => {
    const response = await dsSurveyApiClient.getFromModule('/assignment/roles');
    return response.data || [];
  },

  /**
   * Get specific role by ID
   */
  getRoleById: async (roleId: number): Promise<Role | null> => {
    const response = await dsSurveyApiClient.getFromModule(`/assignment/roles/${roleId}`);
    return response.data;
  },

  // ========================================
  // COMBINED ASSIGNMENT APIs
  // ========================================

  /**
   * Get all role IDs (both surveyor and validator) for a survey
   */
  getAllBySurveyId: async (surveyId: number): Promise<CombinedRoleIds> => {
    const response = await dsSurveyApiClient.getFromModule(`/assignment/survey/${surveyId}`);
    return response.data || { surveyor_role_ids: [], validator_role_ids: [] };
  },

  /**
   * Save all role IDs (both surveyor and validator) for a survey
   */
  saveAll: async (surveyId: number, surveyorRoleIds: number[], validatorRoleIds: number[]): Promise<CombinedRoleIds> => {
    const response = await dsSurveyApiClient.postToModule('/assignment/all', {
      srvy_id: surveyId,
      surveyor_role_ids: surveyorRoleIds,
      validator_role_ids: validatorRoleIds
    });
    return response.data || { surveyor_role_ids: [], validator_role_ids: [] };
  },

  // ========================================
  // SURVEYOR ASSIGNMENT APIs
  // ========================================

  /**
   * Get all surveyor role IDs for a survey
   */
  getSurveyorBySurveyId: async (surveyId: number): Promise<number[]> => {
    const response = await dsSurveyApiClient.getFromModule(`/assignment/surveyor/survey/${surveyId}`);
    return response.data || [];
  },

  /**
   * Save surveyor role IDs for a survey (replaces all existing)
   */
  saveSurveyor: async (surveyId: number, roleIds: number[]): Promise<number[]> => {
    const response = await dsSurveyApiClient.postToModule('/assignment/surveyor', {
      srvy_id: surveyId,
      role_ids: roleIds
    });
    return response.data || [];
  },

  /**
   * Delete a surveyor role assignment
   */
  deleteSurveyor: async (surveyId: number, roleId: number): Promise<void> => {
    await dsSurveyApiClient.deleteFromModule(`/assignment/surveyor/${surveyId}/${roleId}`);
  },

  // ========================================
  // VALIDATOR ASSIGNMENT APIs
  // ========================================

  /**
   * Get all validator role IDs for a survey
   */
  getValidatorBySurveyId: async (surveyId: number): Promise<number[]> => {
    const response = await dsSurveyApiClient.getFromModule(`/assignment/validator/survey/${surveyId}`);
    return response.data || [];
  },

  /**
   * Save validator role IDs for a survey (replaces all existing)
   */
  saveValidator: async (surveyId: number, roleIds: number[]): Promise<number[]> => {
    const response = await dsSurveyApiClient.postToModule('/assignment/validator', {
      srvy_id: surveyId,
      role_ids: roleIds
    });
    return response.data || [];
  },

  /**
   * Delete a validator role assignment
   */
  deleteValidator: async (surveyId: number, roleId: number): Promise<void> => {
    await dsSurveyApiClient.deleteFromModule(`/assignment/validator/${surveyId}/${roleId}`);
  },

  // ========================================
  // VALIDATOR HIERARCHY APIs
  // ========================================

  /**
   * Get all validator assignments with hierarchy fields for a survey
   */
  getValidatorHierarchy: async (surveyId: number): Promise<RoleAssignment[]> => {
    const response = await dsSurveyApiClient.getFromModule(`/assignment/validator-hierarchy/survey/${surveyId}`);
    return response.data || [];
  },

  /**
   * Save validator assignments with hierarchy fields for a survey
   */
  saveValidatorHierarchy: async (surveyId: number, assignments: ValidatorHierarchyConfig[]): Promise<RoleAssignment[]> => {
    const response = await dsSurveyApiClient.postToModule('/assignment/validator-hierarchy', {
      srvy_id: surveyId,
      assignments: assignments
    });
    return response.data || [];
  },

  // ========================================
  // ACCESS CHECK APIs
  // ========================================

  /**
   * Check if the current user can fill (has surveyor access to) a survey
   */
  checkSurveyorAccess: async (surveyId: number): Promise<{ canFill: boolean; reason: string; surveyorRoleIds?: number[] }> => {
    try {
      const response = await dsSurveyApiClient.getFromModule(`/assignment/surveyor/check/${surveyId}`);
      return response.data || { canFill: false, reason: 'Unknown error' };
    } catch (error: any) {
      console.error('[roleAssignmentApi] Error checking surveyor access:', error);
      return { canFill: false, reason: error.message || 'Error checking access' };
    }
  },

  /**
   * Check if the current user can validate (has validator access to) a survey
   */
  checkValidatorAccess: async (surveyId: number): Promise<{ canValidate: boolean; reason: string; validatorRoleIds?: number[] }> => {
    try {
      const response = await dsSurveyApiClient.getFromModule(`/assignment/validator/check/${surveyId}`);
      return response.data || { canValidate: false, reason: 'Unknown error' };
    } catch (error: any) {
      console.error('[roleAssignmentApi] Error checking validator access:', error);
      return { canValidate: false, reason: error.message || 'Error checking access' };
    }
  },
};

// ========================================
// DYNAMIC TABLE APIs
// ========================================
export const dynamicTableApi = {
  /**
   * Generate a data table for a survey based on its questions
   */
  generateTable: async (surveyId: number): Promise<TableGenerationResult> => {
    const response = await dsSurveyApiClient.postToModule(`/table/generate/${surveyId}`, {});
    return response.data;
  },

  /**
   * Get information about a generated table
   */
  getTableInfo: async (surveyId: number): Promise<GeneratedTable | null> => {
    const response = await dsSurveyApiClient.getFromModule(`/table/info/${surveyId}`);
    return response.data;
  },

  /**
   * Get all generated columns for a survey's data table
   */
  getColumns: async (surveyId: number): Promise<GeneratedColumn[]> => {
    const response = await dsSurveyApiClient.getFromModule(`/table/columns/${surveyId}`);
    return response.data || [];
  },

  /**
   * Add a new column to an existing generated table
   */
  addColumn: async (surveyId: number, questionId: number): Promise<GeneratedColumn> => {
    const response = await dsSurveyApiClient.postToModule(`/table/add-column/${surveyId}`, { questionId });
    return response.data;
  },

  /**
   * Add validator columns to an existing generated table
   * This is called automatically when validators are saved, but can be called manually if needed
   */
  addValidatorColumns: async (surveyId: number): Promise<{ added: boolean; newColumns: string[]; reason?: string }> => {
    const response = await dsSurveyApiClient.postToModule(`/table/add-validator-columns/${surveyId}`, {});
    return response.data;
  },

  /**
   * Check if a table exists for a survey
   */
  checkTableExists: async (surveyId: number): Promise<{ exists: boolean; tableName: string | null }> => {
    const response = await dsSurveyApiClient.getFromModule(`/table/exists/${surveyId}`);
    return response.data;
  },

  /**
   * Get question code to column name mapping
   */
  getColumnMapping: async (surveyId: number): Promise<ColumnMapping> => {
    const response = await dsSurveyApiClient.getFromModule(`/table/mapping/${surveyId}`);
    return response.data || {};
  },
};

// ========================================
// DYNAMIC RESPONSE APIs (for generated tables)
// ========================================
export const dynamicResponseApi = {
  /**
   * Insert a new response into a survey's dynamic data table
   */
  insertResponse: async (surveyId: number, data: Record<string, any>): Promise<DynamicResponse> => {
    const response = await dsSurveyApiClient.postToModule(`/data/${surveyId}`, data);
    return response.data;
  },

  /**
   * Update an existing response
   */
  updateResponse: async (surveyId: number, responseId: number, data: Record<string, any>): Promise<DynamicResponse> => {
    const response = await dsSurveyApiClient.putToModule(`/data/${surveyId}/${responseId}`, data);
    return response.data;
  },

  /**
   * Save a draft response (create new draft)
   */
  saveDraft: async (surveyId: number, data: Record<string, any>): Promise<DynamicResponse> => {
    const response = await dsSurveyApiClient.postToModule(`/data/${surveyId}`, {
      ...data,
      rspns_sts_cd: 'DRAFT',
    });
    return response.data;
  },

  /**
   * Update an existing draft (partial save)
   */
  updateDraft: async (surveyId: number, responseId: number, data: Record<string, any>): Promise<DynamicResponse> => {
    const response = await dsSurveyApiClient.patchToModule(`/data/${surveyId}/${responseId}/draft`, data);
    return response.data;
  },

  /**
   * Get current user's drafts for a survey
   */
  getMyDrafts: async (surveyId: number): Promise<DynamicResponse[]> => {
    const response = await dsSurveyApiClient.getFromModule(`/data/${surveyId}/drafts`);
    return response.data || [];
  },

  /**
   * Get responses from a survey's dynamic data table with optional filters
   */
  getResponses: async (surveyId: number, filters?: DynamicResponseFilters): Promise<DynamicResponse[]> => {
    const queryParams = new URLSearchParams();
    if (filters) {
      if (filters.trgt_id) queryParams.append('trgt_id', String(filters.trgt_id));
      if (filters.vldtn_sts_cd) queryParams.append('vldtn_sts_cd', filters.vldtn_sts_cd);
      if (filters.rspndnt_usr_id) queryParams.append('rspndnt_usr_id', String(filters.rspndnt_usr_id));
      if (filters.from_dt) queryParams.append('from_dt', filters.from_dt);
      if (filters.to_dt) queryParams.append('to_dt', filters.to_dt);
      if (filters.limit) queryParams.append('limit', String(filters.limit));
      if (filters.offset) queryParams.append('offset', String(filters.offset));
    }
    const queryString = queryParams.toString();
    const url = queryString ? `/data/${surveyId}?${queryString}` : `/data/${surveyId}`;
    const response = await dsSurveyApiClient.getFromModule(url);
    return response.data || [];
  },

  /**
   * Get enriched responses with resolved names (district, ULB, sachivalayam, ward, user names)
   * Returns enriched structure with userLevel and enrichedColumns metadata
   */
  getEnrichedResponses: async (surveyId: number, filters?: DynamicResponseFilters): Promise<EnrichedResponseData> => {
    const queryParams = new URLSearchParams();
    queryParams.append('enrich', 'true');
    if (filters) {
      if (filters.trgt_id) queryParams.append('trgt_id', String(filters.trgt_id));
      if (filters.vldtn_sts_cd) queryParams.append('vldtn_sts_cd', filters.vldtn_sts_cd);
      if (filters.rspndnt_usr_id) queryParams.append('rspndnt_usr_id', String(filters.rspndnt_usr_id));
      if (filters.userCentric) queryParams.append('userCentric', 'true');
      if (filters.uda_id) queryParams.append('uda_id', String(filters.uda_id));
      if (filters.from_dt) queryParams.append('from_dt', filters.from_dt);
      if (filters.to_dt) queryParams.append('to_dt', filters.to_dt);
      if (filters.limit) queryParams.append('limit', String(filters.limit));
      if (filters.offset) queryParams.append('offset', String(filters.offset));
    }
    const queryString = queryParams.toString();
    const url = `/data/${surveyId}?${queryString}`;
    const response = await dsSurveyApiClient.getFromModule(url);
    const data = response.data;

    // Handle both enriched (object) and flat (array) response formats
    if (data && data.responses && Array.isArray(data.responses)) {
      return data as EnrichedResponseData;
    }

    // Fallback: API returned flat array (enrichment not available)
    return {
      responses: Array.isArray(data) ? data : [],
      userLevel: 'STATE',
      enrichedColumns: [],
      totalCount: Array.isArray(data) ? data.length : 0
    };
  },

  /**
   * Get a single response by ID
   */
  getResponseById: async (surveyId: number, responseId: number): Promise<DynamicResponse | null> => {
    const response = await dsSurveyApiClient.getFromModule(`/data/${surveyId}/${responseId}`);
    return response.data;
  },

  /**
   * Delete a response (soft delete)
   */
  deleteResponse: async (surveyId: number, responseId: number): Promise<void> => {
    await dsSurveyApiClient.deleteFromModule(`/data/${surveyId}/${responseId}`);
  },

  /**
   * Submit a response for validation
   */
  submitResponse: async (surveyId: number, responseId: number): Promise<DynamicResponse> => {
    const response = await dsSurveyApiClient.putToModule(`/data/${surveyId}/${responseId}/submit`, {});
    return response.data;
  },

  /**
   * Get count of responses matching filters
   */
  getResponseCount: async (surveyId: number, filters?: { vldtn_sts_cd?: string; rspndnt_usr_id?: number }): Promise<number> => {
    const queryParams = new URLSearchParams();
    if (filters) {
      if (filters.vldtn_sts_cd) queryParams.append('vldtn_sts_cd', filters.vldtn_sts_cd);
      if (filters.rspndnt_usr_id) queryParams.append('rspndnt_usr_id', String(filters.rspndnt_usr_id));
    }
    const queryString = queryParams.toString();
    const url = queryString ? `/data/${surveyId}/count?${queryString}` : `/data/${surveyId}/count`;
    const response = await dsSurveyApiClient.getFromModule(url);
    return response.data?.count || 0;
  },

  /**
   * Get response counts for all surveys in a single batch call
   * Returns a map of surveyId -> count
   */
  getAllResponseCounts: async (): Promise<Record<number, number>> => {
    const response = await dsSurveyApiClient.getFromModule('/data/counts/all');
    return response.data || {};
  },

  /**
   * Get hierarchy-based survey statistics
   * Returns aggregated stats based on user's level (STATE/REGIONAL/ULB/WARD/SVM)
   */
  getStats: async (surveyId: number): Promise<SurveyStatsResponse> => {
    const response = await dsSurveyApiClient.getFromModule(`/data/${surveyId}/stats`);
    return response.data;
  },

  /**
   * Get drill-down stats for a specific ULB
   */
  getUlbStats: async (surveyId: number, ulbId: number): Promise<SurveyStatsResponse> => {
    const response = await dsSurveyApiClient.getFromModule(`/data/${surveyId}/stats/ulb/${ulbId}`);
    return response.data;
  },

  /**
   * Get drill-down stats for a specific SVM (deepest level)
   */
  getSvmStats: async (surveyId: number, ulbId: number, svmId: number): Promise<SurveyStatsResponse> => {
    const response = await dsSurveyApiClient.getFromModule(`/data/${surveyId}/stats/ulb/${ulbId}/svm/${svmId}`);
    return response.data;
  },
};

// ========================================
// ANALYTICS APIs
// ========================================
export interface OverallSurveyStats {
  surveyId: number;
  surveyName: string;
  description?: string;
  collectionLevel: string;  // 'ULB', 'SVM', 'ONE_TIME', etc.
  status: string;
  startDate?: string;
  endDate?: string;
  totalResponses: number;
  pendingCount: number;
  approvedCount: number;
  rejectedCount: number;
  completionPercentage: number;
  // Activity metrics
  lastActivityDate?: string | null;
  responsesThisWeek?: number;
  daysSinceLastActivity?: number | null;
  // Validation config
  hasValidators?: boolean;  // Whether validation is required for this survey
  // Per-survey participation metrics
  unitsParticipated?: number;  // How many ULBs/SVMs participated in THIS survey
  totalUnits?: number;         // Total available ULBs/SVMs
  unitLabel?: string;          // 'ULB' or 'Sachivalayam'
}

// Hierarchy breakdown item for ULB/SVM participation table
export interface HierarchyBreakdownItem {
  unitId: number;
  unitName: string;
  totalResponses: number;
  surveysParticipated: number;
  totalSurveys: number;
  participationPercentage: number;
}

export interface OverallAnalyticsResponse {
  userLevel: 'STATE' | 'REGIONAL' | 'UDA' | 'ULB' | 'WARD' | 'SVM';
  aggregate: {
    totalSurveys: number;
    activeSurveys: number;
    draftSurveys: number;
    closedSurveys: number;
    totalResponses: number;
    pendingCount: number;
    approvedCount: number;
    rejectedCount: number;
    // Participation metrics
    totalUnits: number;
    respondedUnits: number;
    notRespondedUnits: number;
    unitLabel: string;  // 'ULB' or 'Sachivalayam'
  };
  surveys: OverallSurveyStats[];
  hierarchyBreakdown?: HierarchyBreakdownItem[];
  filters: {
    tenantId: string;
    boundaryScope: {
      districtIds?: number[];
      ulbIds?: number[];
      svmIds?: number[];
    };
  };
}

export interface UdaLookupItem {
  uda_id: number;
  uda_cd: string;
  uda_nm: string;
}

// ========================================
// UDA LOOKUP (DTCP)
// ========================================
export const udaLookupApi = {
  /**
   * UDAs survey responses can belong to. For a UDA-level user only their own UDA
   * is returned, with userUdaId set so filters can be locked to it.
   */
  getUdas: async (): Promise<{ items: UdaLookupItem[]; userUdaId: number | null }> => {
    const response = await dsSurveyApiClient.getFromModule('/lookup/udas');
    const data = response.data || {};
    return { items: Array.isArray(data.items) ? data.items : [], userUdaId: data.userUdaId ?? null };
  },
};

export const analyticsApi = {
  /**
   * Get overall analytics across all surveys
   * Returns aggregated stats for all surveys the user has access to
   */
  getOverallAnalytics: async (signal?: AbortSignal): Promise<OverallAnalyticsResponse> => {
    const sid = getCurrentSiteId();
    const url = sid ? `/analytics/overall?siteId=${sid}` : '/analytics/overall';
    const response = await dsSurveyApiClient.getFromModule(url, { signal });
    return response.data;
  },

  /**
   * Get survey-level stats (same as dynamicResponseApi.getStats but more explicit for analytics)
   */
  getSurveyStats: async (surveyId: number): Promise<SurveyStatsResponse> => {
    const response = await dsSurveyApiClient.getFromModule(`/data/${surveyId}/stats`);
    return response.data;
  },

  /**
   * Get UDA drill-down stats (DTCP: State → UDA → responses)
   */
  getUdaDrilldown: async (surveyId: number, udaId: number): Promise<SurveyStatsResponse> => {
    const response = await dsSurveyApiClient.getFromModule(`/data/${surveyId}/stats/uda/${udaId}`);
    return response.data;
  },

  /**
   * Get ULB drill-down stats
   */
  getUlbDrilldown: async (surveyId: number, ulbId: number): Promise<SurveyStatsResponse> => {
    const response = await dsSurveyApiClient.getFromModule(`/data/${surveyId}/stats/ulb/${ulbId}`);
    return response.data;
  },

  /**
   * Get SVM drill-down stats (deepest level)
   */
  getSvmDrilldown: async (surveyId: number, ulbId: number, svmId: number): Promise<SurveyStatsResponse> => {
    const response = await dsSurveyApiClient.getFromModule(`/data/${surveyId}/stats/ulb/${ulbId}/svm/${svmId}`);
    return response.data;
  },
};

// ========================================
// VALIDATION WORKFLOW APIs
// ========================================
export const validationApi = {
  /**
   * Get the validator hierarchy for a survey
   */
  getHierarchy: async (surveyId: number): Promise<ValidatorHierarchy[]> => {
    const response = await dsSurveyApiClient.getFromModule(`/validation/hierarchy/${surveyId}`);
    return response.data || [];
  },

  /**
   * Check if the current user can validate responses for a survey
   */
  canUserValidate: async (surveyId: number): Promise<CanValidateResult> => {
    const response = await dsSurveyApiClient.getFromModule(`/validation/can-validate/${surveyId}`);
    return response.data;
  },

  /**
   * Approve a response
   */
  approve: async (payload: ValidationActionPayload): Promise<ValidationResult> => {
    const response = await dsSurveyApiClient.postToModule('/validation/approve', payload);
    return response.data;
  },

  /**
   * Reject a response
   */
  reject: async (payload: ValidationActionPayload): Promise<ValidationResult> => {
    const response = await dsSurveyApiClient.postToModule('/validation/reject', payload);
    return response.data;
  },

  /**
   * Bulk approve multiple responses
   */
  bulkApprove: async (payload: BulkValidationPayload): Promise<BulkValidationResult> => {
    const response = await dsSurveyApiClient.postToModule('/validation/bulk-approve', payload);
    return response.data;
  },

  /**
   * Bulk reject multiple responses
   */
  bulkReject: async (payload: BulkValidationPayload): Promise<BulkValidationResult> => {
    const response = await dsSurveyApiClient.postToModule('/validation/bulk-reject', payload);
    return response.data;
  },

  /**
   * Get all pending validations for the current user
   */
  getPending: async (userId?: number): Promise<PendingValidation[]> => {
    const sid = getCurrentSiteId();
    const base = userId ? `/validation/pending/${userId}` : '/validation/pending';
    const url = sid ? `${base}?siteId=${sid}` : base;
    const response = await dsSurveyApiClient.getFromModule(url);
    return response.data || [];
  },

  /**
   * Surveys where the current user is a validator (with their level), even with nothing pending
   */
  getMySurveys: async (): Promise<ValidatorSurvey[]> => {
    const sid = getCurrentSiteId();
    const url = sid ? `/validation/my-surveys?siteId=${sid}` : '/validation/my-surveys';
    const response = await dsSurveyApiClient.getFromModule(url);
    return response.data || [];
  },

  /**
   * Get count of pending validations for a survey
   */
  getPendingCount: async (surveyId: number): Promise<number> => {
    const response = await dsSurveyApiClient.getFromModule(`/validation/pending-count/${surveyId}`);
    return response.data?.count || 0;
  },

  /**
   * Get validation statistics for a survey
   */
  getStats: async (surveyId: number): Promise<ValidationStats> => {
    const response = await dsSurveyApiClient.getFromModule(`/validation/stats/${surveyId}`);
    return response.data;
  },
};

// ========================================
// DASHBOARD APIs
// ========================================
export const dashboardApi = {
  getStats: async (): Promise<SurveyDashboardStats> => {
    // Aggregate stats from various endpoints
    const [surveys, activeSurveys] = await Promise.all([
      surveyApi.getAll(),
      surveyApi.getActive(),
    ]);

    return {
      totalSurveys: surveys.length,
      activeSurveys: activeSurveys.length,
      totalResponses: 0, // Would need backend aggregation
      completedResponses: 0,
      avgCompletionRate: 0,
      recentSurveys: surveys.slice(0, 5),
      recentResponses: [],
    };
  },
};

// ========================================
// BOUNDARY TYPE APIs
// ========================================
export const boundaryTypeApi = {
  /**
   * Get all boundary types from eg_boundarytype table (via ds-survey module)
   */
  getAll: async (): Promise<BoundaryType[]> => {
    console.log('[boundaryTypeApi] getAll called');
    try {
      console.log('[boundaryTypeApi] Making API call to ds-survey/boundary-type');
      const response = await dsSurveyApiClient.getFromModule('/boundary-type');
      console.log('[boundaryTypeApi] API response:', response);
      return response.data || [];
    } catch (error) {
      console.error('[boundaryTypeApi] Error fetching boundary types:', error);
      return [];
    }
  },

  /**
   * Get boundary type by ID
   */
  getById: async (id: number): Promise<BoundaryType | null> => {
    try {
      const response = await dsSurveyApiClient.getFromModule(`/boundary-type/${id}`);
      return response.data || null;
    } catch (error) {
      console.error('Error fetching boundary type:', error);
      return null;
    }
  },
};

// ========================================
// SURVEYOR DASHBOARD APIs
// ========================================
export const surveyorDashboardApi = {
  getDashboard: async (): Promise<SurveyorDashboardData> => {
    const sid = getCurrentSiteId();
    const url = sid ? `/surveyor/dashboard?siteId=${sid}` : '/surveyor/dashboard';
    const response = await dsSurveyApiClient.getFromModule(url);
    return response.data;
  },
  getObligations: async (filters?: {
    srvy_id?: number; oblgtn_sts_cd?: string; from_dt?: string; to_dt?: string;
  }): Promise<SurveyorObligation[]> => {
    const params = new URLSearchParams();
    if (filters?.srvy_id) params.append('srvy_id', String(filters.srvy_id));
    if (filters?.oblgtn_sts_cd) params.append('oblgtn_sts_cd', filters.oblgtn_sts_cd);
    if (filters?.from_dt) params.append('from_dt', filters.from_dt);
    if (filters?.to_dt) params.append('to_dt', filters.to_dt);
    const url = params.toString() ? `/surveyor/obligations?${params}` : '/surveyor/obligations';
    const response = await dsSurveyApiClient.getFromModule(url);
    return response.data || [];
  },
  excuseObligation: async (obligationId: number, reason: string): Promise<any> => {
    const response = await dsSurveyApiClient.postToModule(`/obligations/${obligationId}/excuse`, { reason });
    return response.data;
  },
};

// ========================================
// ATTACHMENT APIs (file/image uploads)
// ========================================
export const attachmentApi = {
  /**
   * Upload a file to S3 and create an unlinked attachment record.
   * Uses BaseAPIService directly so the browser can set multipart/form-data content type.
   */
  upload: async (
    file: File,
    surveyId: number,
    questionId: number,
    type: 'image' | 'document',
    label: string
  ): Promise<{ id: number; url: string; filename: string; label: string; type: string; size: number; mimeType: string }> => {
    const { BaseAPIService } = await import('@/services/baseapiservice');
    const apiService = new BaseAPIService();
    const formData = new FormData();
    formData.append('surveyId', String(surveyId));
    formData.append('questionId', String(questionId));
    formData.append('type', type);
    formData.append('label', label);
    formData.append('file', file);
    const response = await apiService.post(`ds-survey/attachment/upload`, formData);
    return response.data?.data || response.data;
  },

  /**
   * Link previously uploaded attachments to a response ID.
   */
  link: async (attachmentIds: number[], responseId: number, type: 'image' | 'document'): Promise<any> => {
    const response = await dsSurveyApiClient.postToModule('/attachment/link', { attachmentIds, responseId, type });
    return response.data;
  },

  /**
   * Get all attachments for a survey response.
   */
  getByResponse: async (surveyId: number, responseId: number): Promise<{ images: any[]; documents: any[] }> => {
    const response = await dsSurveyApiClient.getFromModule(`/attachment/${surveyId}/${responseId}`);
    return response.data || { images: [], documents: [] };
  },

  /**
   * Soft-delete an attachment.
   */
  delete: async (attachmentId: number, type: 'image' | 'document'): Promise<any> => {
    const response = await dsSurveyApiClient.deleteFromModule(`/attachment/${attachmentId}?type=${type}`);
    return response.data;
  },
};

// ========================================
// HEALTH CHECK
// ========================================
export const healthCheck = async (): Promise<any> => {
  const response = await dsSurveyApiClient.getFromModule('/health');
  return response;
};

// Export default
export default {
  fieldTypeApi,
  targetTypeApi,
  surveyApi,
  sectionApi,
  questionApi,
  responseApi,
  answerApi,
  roleAssignmentApi,
  boundaryTypeApi,
  dynamicTableApi,
  dynamicResponseApi,
  validationApi,
  dashboardApi,
  surveyorDashboardApi,
  analyticsApi,
  attachmentApi,
  healthCheck,
};
